import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseDate } from "../utils/dateUtils";
import { 
  Plus, Edit, Trash2, Calendar, Clock, MapPin, Repeat
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tipoEventoOptions = [
  { value: "culto", label: "Culto" },
  { value: "reuniao", label: "Reunião" },
  { value: "treinamento", label: "Treinamento" },
  { value: "evento", label: "Evento Especial" },
  { value: "celula", label: "Célula" },
  { value: "outro", label: "Outro" }
];

const recorrenciaOptions = [
  { value: "diario", label: "Diariamente" },
  { value: "semanal", label: "Semanalmente" },
  { value: "quinzenal", label: "Quinzenalmente" },
  { value: "mensal", label: "Mensalmente" }
];

const diasSemanaOptions = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda-feira" },
  { value: 2, label: "Terça-feira" },
  { value: 3, label: "Quarta-feira" },
  { value: 4, label: "Quinta-feira" },
  { value: 5, label: "Sexta-feira" },
  { value: 6, label: "Sábado" }
];

const initialFormState = {
  titulo: "",
  descricao: "",
  data_inicio: "",
  data_fim: "",
  local: "",
  tipo: "culto",
  exibir_calendario: true,
  recorrente: false,
  recorrencia: {
    tipo: "semanal",
    dia_semana: 0,
    quantidade: 4  // Número de repetições
  }
};

export default function EventsPage() {
  const { currentBranch } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const nova = searchParams.get("nova");
    if (nova && /^\d{4}-\d{2}-\d{2}$/.test(nova)) {
      setEditingEvent(null);
      setForm({ ...initialFormState, data_inicio: `${nova}T19:00`, data_fim: `${nova}T21:00` });
      setDialogOpen(true);
      searchParams.delete("nova");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Abre edição vinda do calendário da tela inicial (/eventos?editar=<id>)
  useEffect(() => {
    const editar = searchParams.get("editar");
    if (!editar) return;
    axios.get(`${API}/events/${editar}`)
      .then(r => { handleEdit(r.data); setDialogOpen(true); })
      .catch(() => toast.error("Evento não encontrado"))
      .finally(() => { searchParams.delete("editar"); setSearchParams(searchParams, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    loadEvents();
  }, [currentBranch]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const filialParam = currentBranch ? `?filial_id=${currentBranch.id}` : '';
      const res = await axios.get(`${API}/events${filialParam}`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Erro ao carregar eventos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...form, 
        filial_id: currentBranch?.id || null
      };
      
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, data);
        toast.success("Evento atualizado!");
      } else {
        // Se for recorrente, criar múltiplos eventos
        if (form.recorrente && form.recorrencia?.quantidade > 1) {
          const response = await axios.post(`${API}/events/recurring`, data);
          const count = response.data?.created_count || 1;
          toast.success(`${count} eventos criados com sucesso!`);
        } else {
          await axios.post(`${API}/events`, data);
          toast.success("Evento criado!");
        }
      }
      setDialogOpen(false);
      setEditingEvent(null);
      setForm(initialFormState);
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setForm({
      titulo: event.titulo || event.nome || "",
      descricao: event.descricao || "",
      data_inicio: event.data_inicio || "",
      data_fim: event.data_fim || "",
      local: event.local || "",
      tipo: event.tipo || "culto",
      exibir_calendario: event.exibir_calendario !== false,
      recorrente: event.recorrente || false,
      recorrencia: {
        tipo: event.recorrencia?.tipo || "semanal",
        dia_semana: event.recorrencia?.dia_semana || 0,
        dia_mes: event.recorrencia?.dia_mes || 1,
        quantidade: event.recorrencia?.quantidade || 4
      }
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover este evento?")) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success("Evento removido");
      loadEvents();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const getEventStatus = (event) => {
    const now = new Date();
    // Support both old and new field names
    const startStr = event.data_inicio || event.data;
    const endStr = event.data_fim || event.data_termino;
    const start = startStr ? parseDate(startStr) : null;
    const end = endStr ? parseDate(endStr) : null;
    
    if (!start) return "pendente";
    if (end && now > end) return "encerrado";
    if (now < start) return "agendado";
    return "em_andamento";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "em_andamento":
        return <Badge className="bg-green-500">Em andamento</Badge>;
      case "agendado":
        return <Badge variant="outline">Agendado</Badge>;
      case "encerrado":
        return <Badge variant="secondary">Encerrado</Badge>;
      default:
        return <Badge variant="outline">Pendente</Badge>;
    }
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return "--";
    try {
      const date = parseDate(dateStr);
      if (!date) return dateStr;
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  // Get date from event (support both old and new field names)
  const getEventDate = (event) => {
    return event.data_inicio || event.data || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="eventos-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Eventos</h1>
        <Button onClick={() => { setEditingEvent(null); setForm(initialFormState); setDialogOpen(true); }} data-testid="add-event-button">
          <Plus className="h-4 w-4 mr-2" />
          Novo Evento
        </Button>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">Nenhum evento cadastrado</p>
            <Button 
              className="mt-4" 
              onClick={() => { setEditingEvent(null); setForm(initialFormState); setDialogOpen(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar Primeiro Evento
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {events.map((event) => {
            const status = getEventStatus(event);
            const titulo = event.titulo || event.nome || "Sem título";
            const eventDate = getEventDate(event);
            
            return (
              <Card key={event.id} className="card-hover" data-testid={`event-card-${event.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Date Box */}
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                      <span className="text-xs text-muted-foreground uppercase">
                        {eventDate ? format(parseDate(eventDate), "MMM", { locale: ptBR }) : "---"}
                      </span>
                      <span className="text-xl font-bold text-primary">
                        {eventDate ? format(parseDate(eventDate), "dd") : "--"}
                      </span>
                    </div>
                    
                    {/* Event Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{titulo}</h3>
                        {getStatusBadge(status)}
                        <Badge variant="outline" className="text-xs">
                          {tipoEventoOptions.find(t => t.value === event.tipo)?.label || event.tipo || "Evento"}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatEventDate(eventDate)}
                        </span>
                        {event.local && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.local}
                          </span>
                        )}
                        {event.recorrente && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Repeat className="h-3 w-3" />
                            {recorrenciaOptions.find(r => r.value === event.recorrencia?.tipo)?.label || "Recorrente"}
                          </span>
                        )}
                      </div>
                      
                      {/* Check-in indicators - configuração herdada da unidade */}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          Check-in automático
                        </Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(event)}
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(event.id)}
                        title="Remover"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ===== ADD/EDIT EVENT DIALOG ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Editar Evento" : "Novo Evento"}</DialogTitle>
            <DialogDescription>
              Configure os detalhes do evento. O check-in automático usa as configurações da unidade.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="titulo">Título do Evento *</Label>
              <Input
                id="titulo"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Culto de Domingo"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipoEventoOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="local">Local</Label>
                <Input
                  id="local"
                  value={form.local}
                  onChange={(e) => setForm({ ...form, local: e.target.value })}
                  placeholder="Ex: Templo Principal"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="data_inicio">Data/Hora Início *</Label>
                <Input
                  id="data_inicio"
                  type="datetime-local"
                  value={form.data_inicio ? form.data_inicio.slice(0, 16) : ""}
                  onChange={(e) => setForm({ ...form, data_inicio: e.target.value + ":00Z" })}
                  required
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_fim">Data/Hora Fim *</Label>
                <Input
                  id="data_fim"
                  type="datetime-local"
                  value={form.data_fim ? form.data_fim.slice(0, 16) : ""}
                  onChange={(e) => setForm({ ...form, data_fim: e.target.value + ":00Z" })}
                  required
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Detalhes do evento..."
                rows={2}
              />
            </div>

            {/* Recorrência */}
            <div className="space-y-4 pt-2 border-t">
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <Label htmlFor="exibir_calendario" className="cursor-pointer font-medium">Exibir no calendário</Label>
                  <p className="text-xs text-muted-foreground">Aparece no calendário da tela inicial e do News</p>
                </div>
                <Switch id="exibir_calendario" checked={form.exibir_calendario} onCheckedChange={(v) => setForm({ ...form, exibir_calendario: v })} data-testid="event-exibir-calendario" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="recorrente" className="cursor-pointer font-medium">
                    Evento Recorrente
                  </Label>
                </div>
                <Switch
                  id="recorrente"
                  checked={form.recorrente}
                  onCheckedChange={(checked) => setForm({ ...form, recorrente: checked })}
                />
              </div>

              {form.recorrente && (
                <div className="grid grid-cols-2 gap-4 pl-6 animate-in fade-in slide-in-from-top-2">
                  <div className="space-y-2">
                    <Label>Frequência</Label>
                    <Select 
                      value={form.recorrencia?.tipo || "semanal"} 
                      onValueChange={(v) => setForm({ 
                        ...form, 
                        recorrencia: { ...form.recorrencia, tipo: v } 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {recorrenciaOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Repetições</Label>
                    <Input
                      type="number"
                      min="1"
                      max="52"
                      value={form.recorrencia?.quantidade || 4}
                      onChange={(e) => setForm({ 
                        ...form, 
                        recorrencia: { ...form.recorrencia, quantidade: parseInt(e.target.value) || 4 } 
                      })}
                      placeholder="Ex: 4"
                    />
                  </div>

                  {(form.recorrencia?.tipo === "semanal" || form.recorrencia?.tipo === "quinzenal") && (
                    <div className="space-y-2 col-span-2">
                      <Label>Dia da Semana</Label>
                      <Select 
                        value={String(form.recorrencia?.dia_semana ?? 0)} 
                        onValueChange={(v) => setForm({ 
                          ...form, 
                          recorrencia: { ...form.recorrencia, dia_semana: parseInt(v) } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {diasSemanaOptions.map(opt => (
                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {form.recorrencia?.tipo === "mensal" && (
                    <div className="space-y-2 col-span-2">
                      <Label>Dia do Mês</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={form.recorrencia?.dia_mes || 1}
                        onChange={(e) => setForm({ 
                          ...form, 
                          recorrencia: { ...form.recorrencia, dia_mes: parseInt(e.target.value) } 
                        })}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingEvent ? "Salvar" : "Criar Evento"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
